import {useEffect, useMemo, useState} from "react";
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies,
    getFilteredObservations,
    getObservationsByLocation,
    getObservationsBySpecies
} from "ebird-mydata-reader";
import * as localforage from "localforage";
import {getHotspotHabitatFeedbackSummary} from "../api/hotspot-habitat-feedback-summary";
import {getHotspotHabitatOverrideSummary} from "../api/hotspot-habitat-override-summary";
import {DEBUG_PERF, endPerfTimer, startPerfTimer} from "./perf";
import {
    CachedDerivedBirdData,
    CachedObservationsByLocation,
    CachedObservationsBySpecies,
    DERIVED_BIRD_DATA_CACHE_KEY,
    DERIVED_BIRD_DATA_CACHE_VERSION,
    CachedLocationEcoData,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_VERSION,
    CachedLocationRegionData,
    LOCATION_REGION_DATA_CACHE_KEY,
    LOCATION_REGION_DATA_CACHE_VERSION
} from "./birdDataCache";
import type {
    EnrichedObservationsByLocation,
    HotspotHabitatFeedbackSummary,
    HotspotHabitatOverride,
    LocationEcoregionData,
    LocationRegionData
} from "./types";
import {buildObservationDatasetSignature} from "./storedBirdData";

function getObservationCacheKey(observation: Partial<EBirdMyDataSchema>) {
    return [
        observation.submissionId ?? observation.submissionID ?? '',
        observation.locationId ?? observation.location ?? '',
        observation.date ?? '',
        observation.time ?? '',
        observation.canonicalTaxonomicOrder ?? '',
        observation.count ?? ''
    ].join('|');
}

function cacheObservationsBySpecies(observationsBySpecies: EBirdObservationsBySpecies[], rawData: EBirdMyDataSchema[]): CachedObservationsBySpecies[] {
    const observationIndexesByKey = new Map<string, number[]>();

    rawData.forEach((observation, index) => {
        const key = getObservationCacheKey(observation);
        const matches = observationIndexesByKey.get(key) ?? [];
        matches.push(index);
        observationIndexesByKey.set(key, matches);
    });

    return observationsBySpecies.map(speciesGroup => ({
        taxonomicOrder: speciesGroup.taxonomicOrder,
        commonName: speciesGroup.commonName,
        observationIndexes: speciesGroup.observations
            .map(observation => {
                const key = getObservationCacheKey(observation);
                const indexes = observationIndexesByKey.get(key);

                if (!indexes?.length) return undefined;

                return indexes.shift();
            })
            .filter((index): index is number => Number.isFinite(index))
    }));
}

function cacheObservationsByLocation(observationsByLocation: EBirdObservationsByLocation[], rawData: EBirdMyDataSchema[]): CachedObservationsByLocation[] {
    const observationIndexByReference = new Map<EBirdMyDataSchema, number>();
    rawData.forEach((observation, index) => {
        observationIndexByReference.set(observation, index);
    });

    return observationsByLocation.map(locationGroup => ({
        locationId: locationGroup.locationId,
        location: locationGroup.location,
        observationIndexes: locationGroup.observations
            .map(observation => observationIndexByReference.get(observation))
            .filter((index): index is number => Number.isFinite(index))
    }));
}

function hydrateObservationsBySpecies(cachedObservationsBySpecies: CachedObservationsBySpecies[], rawData: EBirdMyDataSchema[]): EBirdObservationsBySpecies[] {
    return cachedObservationsBySpecies.map(speciesGroup => ({
        taxonomicOrder: speciesGroup.taxonomicOrder,
        commonName: speciesGroup.commonName,
        observations: (speciesGroup.observationIndexes ?? [])
            .map(index => rawData[index])
            .filter((observation): observation is EBirdMyDataSchema => Boolean(observation))
    }));
}

function hydrateObservationsByLocation(cachedObservationsByLocation: CachedObservationsByLocation[], rawData: EBirdMyDataSchema[]): EBirdObservationsByLocation[] {
    return cachedObservationsByLocation.map(locationGroup => ({
        locationId: locationGroup.locationId,
        location: locationGroup.location,
        observations: (locationGroup.observationIndexes ?? [])
            .map(index => rawData[index])
            .filter((observation): observation is EBirdMyDataSchema => Boolean(observation))
    }));
}

function isCachedObservationsBySpecies(value: unknown): value is CachedObservationsBySpecies[] {
    return Array.isArray(value) && value.every(item => (
        item
        && typeof item === 'object'
        && Number.isFinite((item as CachedObservationsBySpecies).taxonomicOrder)
        && typeof (item as CachedObservationsBySpecies).commonName === 'string'
        && Array.isArray((item as CachedObservationsBySpecies).observationIndexes)
    ));
}

function isCachedObservationsByLocation(value: unknown): value is CachedObservationsByLocation[] {
    return Array.isArray(value) && value.every(item => (
        item
        && typeof item === 'object'
        && typeof (item as CachedObservationsByLocation).locationId === 'string'
        && typeof (item as CachedObservationsByLocation).location === 'string'
        && Array.isArray((item as CachedObservationsByLocation).observationIndexes)
    ));
}

export function useBirdData(rawData: any[]) {
    const [observationsBySpecies, setObservationsBySpecies] = useState<EBirdObservationsBySpecies[]>([]);
    const [observationsByLocation, setObservationsByLocation] = useState<EBirdObservationsByLocation[]>([]);
    const [ecoDataByLocationId, setEcoDataByLocationId] = useState<Record<string, LocationEcoregionData>>({});
    const [regionDataByLocationId, setRegionDataByLocationId] = useState<Record<string, LocationRegionData>>({});
    const [habitatFeedbackSummaryByLocationId, setHabitatFeedbackSummaryByLocationId] = useState<Record<string, HotspotHabitatFeedbackSummary>>({});
    const [habitatOverrideByLocationId, setHabitatOverrideByLocationId] = useState<Record<string, HotspotHabitatOverride>>({});
    const lifeList = useMemo((): EBirdMyDataSchema[] => {
        return rawData.length ? getFilteredObservations(rawData, 'life') : [];
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveLocationEcoData = async () => {
            if (!rawData.length) {
                setEcoDataByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadEcoData');
            try {
                const cachedEcoData: CachedLocationEcoData | null = await localforage.getItem(LOCATION_ECO_DATA_CACHE_KEY);

                if (!isActive) return;

                setEcoDataByLocationId(
                    cachedEcoData?.version === LOCATION_ECO_DATA_CACHE_VERSION
                        ? cachedEcoData.ecoDataByLocationId ?? {}
                        : {}
                );
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setEcoDataByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        }

        void resolveLocationEcoData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveLocationRegionData = async () => {
            if (!rawData.length) {
                setRegionDataByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadRegionData');
            try {
                const cachedRegionData: CachedLocationRegionData | null = await localforage.getItem(LOCATION_REGION_DATA_CACHE_KEY);

                if (!isActive) return;

                setRegionDataByLocationId(
                    cachedRegionData?.version === LOCATION_REGION_DATA_CACHE_VERSION
                        ? cachedRegionData.regionDataByLocationId ?? {}
                        : {}
                );
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setRegionDataByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        }

        void resolveLocationRegionData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveDerivedBirdData = async () => {
            if (!rawData.length) {
                setObservationsBySpecies([]);
                setObservationsByLocation([]);
                return;
            }

            const totalTimer = startPerfTimer('birdData:resolveDerivedBirdData');
            const sourceSignature = buildObservationDatasetSignature(rawData);
            try {
                const cacheTimer = startPerfTimer('birdData:derivedCacheLookup');
                const cachedDerivedBirdData: CachedDerivedBirdData | null = await localforage.getItem(DERIVED_BIRD_DATA_CACHE_KEY);
                endPerfTimer(cacheTimer);

                if (
                    cachedDerivedBirdData?.version === DERIVED_BIRD_DATA_CACHE_VERSION
                    && cachedDerivedBirdData.sourceObservationCount === rawData.length
                    && cachedDerivedBirdData.sourceSignature === sourceSignature
                    && isCachedObservationsBySpecies(cachedDerivedBirdData.observationsBySpecies)
                    && isCachedObservationsByLocation(cachedDerivedBirdData.observationsByLocation)
                ) {
                    const hydratedObservationsBySpecies = hydrateObservationsBySpecies(cachedDerivedBirdData.observationsBySpecies, rawData);
                    const hydratedObservationsByLocation = hydrateObservationsByLocation(cachedDerivedBirdData.observationsByLocation, rawData);

                    if (DEBUG_PERF) {
                        console.debug('birdData:derivedCacheHit', {
                            observationsBySpecies: hydratedObservationsBySpecies.length,
                            observationsByLocation: hydratedObservationsByLocation.length
                        });
                    }

                    if (!isActive) return;

                    setObservationsBySpecies(hydratedObservationsBySpecies);
                    setObservationsByLocation(hydratedObservationsByLocation);
                    return;
                }

                if (DEBUG_PERF) console.debug('birdData:derivedCacheMiss');
                const speciesTimer = startPerfTimer('birdData:getObservationsBySpecies');
                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                endPerfTimer(speciesTimer);
                const locationTimer = startPerfTimer('birdData:getObservationsByLocation');
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                endPerfTimer(locationTimer);
                const cacheWriteTimer = startPerfTimer('birdData:derivedCacheWrite');
                await localforage.setItem(DERIVED_BIRD_DATA_CACHE_KEY, {
                    version: DERIVED_BIRD_DATA_CACHE_VERSION,
                    sourceObservationCount: rawData.length,
                    sourceSignature,
                    observationsBySpecies: cacheObservationsBySpecies(nextObservationsBySpecies, rawData),
                    observationsByLocation: cacheObservationsByLocation(nextObservationsByLocation, rawData)
                });
                endPerfTimer(cacheWriteTimer);

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
            } catch (error) {
                console.error(error);

                if (DEBUG_PERF) console.debug('birdData:derivedFallbackRebuild');
                const speciesTimer = startPerfTimer('birdData:getObservationsBySpecies:fallback');
                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                endPerfTimer(speciesTimer);
                const locationTimer = startPerfTimer('birdData:getObservationsByLocation:fallback');
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                endPerfTimer(locationTimer);

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
            } finally {
                endPerfTimer(totalTimer);
            }
        }

        void resolveDerivedBirdData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveHabitatOverrides = async () => {
            if (!observationsByLocation.length) {
                setHabitatOverrideByLocationId({});
                return;
            }

            const locationIds = Array.from(new Set(
                observationsByLocation
                    .map(locationGroup => locationGroup.locationId)
                    .filter(locationId => {
                        if (!locationId) return false;

                        return !Object.prototype.hasOwnProperty.call(ecoDataByLocationId[locationId] ?? {}, 'habitatOverride');
                    })
            ));

            if (!locationIds.length) {
                setHabitatOverrideByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadHabitatOverrides');
            try {
                const response = await getHotspotHabitatOverrideSummary(locationIds);
                if (!isActive) return;

                setHabitatOverrideByLocationId(response.overridesByLocationId ?? {});
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setHabitatOverrideByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        };

        void resolveHabitatOverrides();

        return () => {
            isActive = false;
        };
    }, [ecoDataByLocationId, observationsByLocation]);

    useEffect(() => {
        let isActive = true;

        const resolveHabitatFeedbackSummary = async () => {
            if (!observationsByLocation.length) {
                setHabitatFeedbackSummaryByLocationId({});
                return;
            }

            const locationIds = Array.from(new Set(
                observationsByLocation
                    .map(locationGroup => locationGroup.locationId)
                    .filter(locationId => {
                        if (!locationId) return false;

                        return !Object.prototype.hasOwnProperty.call(ecoDataByLocationId[locationId] ?? {}, 'habitatFeedbackSummary');
                    })
            ));

            if (!locationIds.length) {
                setHabitatFeedbackSummaryByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadHabitatFeedbackSummary');
            try {
                const response = await getHotspotHabitatFeedbackSummary(locationIds);
                if (!isActive) return;

                setHabitatFeedbackSummaryByLocationId(response.summariesByLocationId ?? {});
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setHabitatFeedbackSummaryByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        };

        void resolveHabitatFeedbackSummary();

        return () => {
            isActive = false;
        };
    }, [ecoDataByLocationId, observationsByLocation]);

    const activeObservationsByLocation = useMemo((): EBirdObservationsByLocation[] | EnrichedObservationsByLocation[] => {
        const timer = startPerfTimer('birdData:buildActiveObservationsByLocation');
        if (
            !Object.keys(ecoDataByLocationId).length
            && !Object.keys(regionDataByLocationId).length
            && !Object.keys(habitatOverrideByLocationId).length
            && !Object.keys(habitatFeedbackSummaryByLocationId).length
        ) {
            endPerfTimer(timer);
            return observationsByLocation;
        }

        const result = observationsByLocation.map(locationGroup => {
            const ecoData = ecoDataByLocationId[locationGroup.locationId];
            const regionData = regionDataByLocationId[locationGroup.locationId];
            const habitatOverride = ecoData?.habitatOverride ?? habitatOverrideByLocationId[locationGroup.locationId];
            const habitatFeedbackSummary = ecoData?.habitatFeedbackSummary ?? habitatFeedbackSummaryByLocationId[locationGroup.locationId];
            if (!ecoData && !regionData && !habitatOverride && !habitatFeedbackSummary) return locationGroup;

            return {
                ...locationGroup,
                ...(ecoData ? {ecoData} : {}),
                ...(regionData ? {regionData} : {}),
                ...(habitatOverride ? {habitatOverride} : {}),
                ...(habitatFeedbackSummary ? {habitatFeedbackSummary} : {})
            };
        });
        endPerfTimer(timer);
        return result;
    }, [ecoDataByLocationId, habitatFeedbackSummaryByLocationId, habitatOverrideByLocationId, observationsByLocation, regionDataByLocationId]);

    return {
        lifeList,
        observationsBySpecies,
        observationsByLocation: activeObservationsByLocation
    }
}
